
// Package imports:
import React, { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
// Style imports:
// Component imports:
import Table from 'react-bootstrap/Table';
// Service imports:
import { formatNumber } from '../../services/utils';
// Action imports:
import { setSelectedTick } from '../../redux-store/actions/dashboardActions';
// Type imports:
import { RootState } from '../../redux-store/reducers';
import { WithBook } from '../../types/api/Types';
import { PublicOrderByLevelTickData } from '../../types/api/PublicTypes';

interface IOwnProps {
    sortedPublicOrdersByLevelTickData: WithBook<PublicOrderByLevelTickData>[]
};
type ReduxProps = ConnectedProps<typeof connector>
type Props = ReduxProps & IOwnProps;


const ByLevelBook: React.FC<Props> = ({
    sortedPublicOrdersByLevelTickData,
    selectedTick,
    setSelectedTick
}) => {
    const [ hoveredBookEntry, setHoveredBookEntry ] = useState<PublicOrderByLevelTickData | null>(null);


    const getTdClassName = (order: any, selectedTick: any, hoveredBookEntry: any) => {
        if (order?.tick === undefined) return undefined;
        if (selectedTick === order.tick && hoveredBookEntry === order.tick) return 'CurrentHover';
        if (selectedTick === order.tick) return 'Current';
        if (hoveredBookEntry === order.tick) return 'Hover';
        return undefined;
    }

    const getFirstRow = (
        topBidTdClassName: any, 
        topAskTdClassName: any,
        topBidOrder: WithBook<PublicOrderByLevelTickData> | undefined,
        topAskOrder: WithBook<PublicOrderByLevelTickData> | undefined

    ) => {
        return [
            <tr>
                <td
                    className={topBidTdClassName}
                    onClick={() => {
                        if (topBidOrder?.tick) setSelectedTick(topBidOrder.tick);
                    }}
                    onMouseEnter={() => setHoveredBookEntry(topBidOrder?.tick || null)}
                    onMouseLeave={() => setHoveredBookEntry(null)}
                >
                    {(topBidOrder) ? formatNumber(topBidOrder?.tick?.quantity, 'volume') : null}
                </td>
                <td
                    className={topBidTdClassName}
                    onClick={() => {
                        if (topBidOrder?.tick) setSelectedTick(topBidOrder.tick);
                    }}
                    onMouseEnter={() => setHoveredBookEntry(topBidOrder?.tick || null)}
                    onMouseLeave={() => setHoveredBookEntry(null)}
                >
                    {(topBidOrder) ? formatNumber(topBidOrder?.tick?.price) : null}
                </td>
                <td
                    className={topAskTdClassName}
                    onClick={() => { if (topAskOrder?.tick) setSelectedTick(topAskOrder.tick) }}
                    onMouseEnter={() => setHoveredBookEntry(topAskOrder?.tick || null)}
                    onMouseLeave={() => setHoveredBookEntry(null)}
                >
                    {(topAskOrder) ? formatNumber(topAskOrder?.tick?.price) : null}
                </td>
                <td
                    className={topAskTdClassName}
                    onClick={() => { if (topAskOrder?.tick) setSelectedTick(topAskOrder.tick); }}
                    onMouseEnter={() => setHoveredBookEntry(topAskOrder?.tick || null)}
                    onMouseLeave={() => setHoveredBookEntry(null)}
                >
                    {(topAskOrder) ? formatNumber(topAskOrder?.tick?.quantity, 'volume') : null}
                </td>
            </tr>
        ];
    }

    const getBookRows = () => {
        // Try to find the selected order. If the selected tick is not OrderTickData, then cut off the orders at the timestamp.
        const indexOfSelectedOrder = sortedPublicOrdersByLevelTickData.findIndex(({ tick }) => {
            if (selectedTick === null) return false;
            if (selectedTick.timestamp !== null && tick.timestamp !== null && new Date(selectedTick.timestamp) < new Date(tick.timestamp)) {
                return true;
            }
            return false;
        });

        // No tick found, the book is empty
        if (indexOfSelectedOrder === -1) {
            return (
                <tr className='NoClick'>
                    <td className='text-left EmptyTableText' colSpan={4}>
                        Empty book
                    </td>
                </tr>
            )
        }

        // Find the last 
        const sortedPublicOrdersByLevelUntilSelectedTick = sortedPublicOrdersByLevelTickData.slice(0, indexOfSelectedOrder);
        
        // Find last ask and bid in the partial array.
        const reverseSortedPublicOrdersByLevelUntilSelectedTick = [...sortedPublicOrdersByLevelUntilSelectedTick].reverse();

        const topAskOrder = reverseSortedPublicOrdersByLevelUntilSelectedTick.find(tickbook => tickbook.tick.side === 'ask' && tickbook.tick.level === 1);
        const topBidOrder = reverseSortedPublicOrdersByLevelUntilSelectedTick.find(tickbook => tickbook.tick.side === 'bid' && tickbook.tick.level === 1);

        const deepBookBidAndAskOrders = [];
        for (let level = 2; level <= 5; level++) {
            const askOrder = reverseSortedPublicOrdersByLevelUntilSelectedTick.find(tickbook => tickbook.tick.side === 'ask' && tickbook.tick.level === level);
            const bidOrder = reverseSortedPublicOrdersByLevelUntilSelectedTick.find(tickbook => tickbook.tick.side === 'bid' && tickbook.tick.level === level);
            deepBookBidAndAskOrders.push({
                askOrder,
                bidOrder
            });
        }

        // First add top orders since they are the only ones that can be clicked.
        // Calculate styles based on selectedTick and hover
        const topBidTdClassName = getTdClassName(topBidOrder, selectedTick, hoveredBookEntry);
        const topAskTdClassName = getTdClassName(topAskOrder, selectedTick, hoveredBookEntry);

        const firstRow = getFirstRow(topBidTdClassName, topAskTdClassName, topBidOrder, topAskOrder);

        const jsxRows = firstRow.concat(deepBookBidAndAskOrders.map(({ askOrder, bidOrder }) => {
            return (
                <tr className='disabled'>
                    <td>{(bidOrder) ? formatNumber(bidOrder?.tick?.quantity, 'volume') : null}</td>
                    <td>{(bidOrder) ? formatNumber(bidOrder?.tick?.price) : null}</td>
                    <td>{(askOrder) ? formatNumber(askOrder?.tick?.price) : null}</td>
                    <td>{(askOrder) ? formatNumber(askOrder?.tick?.quantity, 'volume') : null}</td>
                </tr>
            )
        }));

        return jsxRows;
    }
    
    return (
        <div id='ByLevelBook'>
            <h5 className='SectionTitle'>Order Book</h5>
            <Table className='CompressedTable NotHover'>
                <thead>
                    <tr>
                        <th scope='col'>Qty.</th>
                        <th scope='col'>Bid</th>
                        <th scope='col'>Ask</th>
                        <th scope='col'>Qty.</th>
                    </tr>
                </thead>
                <tbody>
                    {getBookRows()}
                </tbody>
            </Table>
        </div>
    );
}

function mapStateToProps({ dashboardReducer }: RootState) {
    const { selectedTick } = dashboardReducer;
    return {
        selectedTick
    };
}

const connector = connect(mapStateToProps, { setSelectedTick });
export default connector(ByLevelBook);